import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ImageModal from './ImageModal';
import './ImageViewer.css';

const ImageViewer = ({ src, alt, className, images = null, originalSrc = null, onLoad, onError, ...props }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageRect, setImageRect] = useState(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = useRef(null);
    const prevSrcRef = useRef(src);

    useEffect(() => {
        if (prevSrcRef.current !== src) {
            prevSrcRef.current = src;
            setIsLoaded(false);
        }
        // Cached image may finish loading before the handler fires
        const img = imgRef.current;
        if (img && img.complete && img.naturalWidth > 0) {
            setIsLoaded(true);
        }
    }, [src]);

    const handleLoad = (event) => {
        setIsLoaded(true);
        onLoad?.(event);
    };

    const handleError = (event) => {
        setIsLoaded(true);
        onError?.(event);
    };

    const handleOpen = () => {
        if (imgRef.current) {
            const rect = imgRef.current.getBoundingClientRect();
            const computedStyle = window.getComputedStyle(imgRef.current);
            setImageRect({
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                objectFit: computedStyle.objectFit
            });

            if (images && images.length > 0) {
                const index = images.indexOf(originalSrc || src);
                setCurrentIndex(index >= 0 ? index : 0);
            }

            setIsOpen(true);
        }
    };

    const handleNavigate = (newIndex) => {
        setCurrentIndex(newIndex);
    };

    const galleryImages = images && images.length > 0 ? images : [originalSrc || src];

    return (
        <>
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                className={`${className || ''} image-viewer ${isLoaded ? 'image-viewer--loaded' : ''}`}
                onClick={handleOpen}
                onLoad={handleLoad}
                onError={handleError}
                {...props}
            />
            {isOpen && createPortal(
                <ImageModal
                    images={galleryImages}
                    currentIndex={currentIndex}
                    onClose={() => setIsOpen(false)}
                    onNavigate={handleNavigate}
                    initialRect={imageRect}
                />,
                document.body
            )}
        </>
    );
};

export default ImageViewer;
